import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Camera, X } from "@phosphor-icons/react";

/**
 * CameraScan
 * Live camera barcode scanner using html5-qrcode (CODE128 + common formats).
 * On a successful read, calls onDetected(text) once and stops the camera.
 */
export default function CameraScan({ onDetected, onClose }) {
  const elementId = "pinboard-camera-scan-region";
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);
  const detectedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(elementId, {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.QR_CODE,
      ],
      verbose: false,
    });
    scannerRef.current = scanner;

    const start = async () => {
      try {
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const m = Math.min(vw, vh);
              return { width: Math.floor(m * 0.8), height: Math.floor(m * 0.45) };
            },
          },
          (decoded) => {
            if (cancelled || detectedRef.current) return;
            detectedRef.current = true;
            // Stop in background, then notify
            scanner
              .stop()
              .catch(() => {})
              .finally(() => onDetected && onDetected(String(decoded || "").trim()));
          },
          () => {
            // ignore per-frame decode errors
          }
        );
        if (!cancelled) setStarting(false);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.message?.includes("Permission")
              ? "ΧΡΕΙΑΖΕΤΑΙ ΑΔΕΙΑ ΓΙΑ ΤΗΝ ΚΑΜΕΡΑ"
              : "ΔΕΝ ΗΤΑΝ ΕΦΙΚΤΗ Η ΕΚΚΙΝΗΣΗ ΚΑΜΕΡΑΣ"
          );
          setStarting(false);
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      try {
        if (scanner && scanner.getState && scanner.getState() === 2 /* SCANNING */) {
          scanner.stop().catch(() => {});
        }
      } catch (_) {
        // ignore
      }
    };
  }, [onDetected]);

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="camera-scan-wrap"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/65 inline-flex items-center gap-1.5">
          <Camera size={14} weight="bold" />
          ΣΤΟΧΕΥΣΕ ΤΟ BARCODE
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-testid="camera-scan-close"
            className="p-1.5 rounded-full text-ink/60 hover:bg-black/5"
            aria-label="ΚΛΕΙΣΙΜΟ"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      <div
        id={elementId}
        className="relative w-full bg-black/90 rounded-xl overflow-hidden"
        style={{ minHeight: "260px" }}
      />
      {starting && !error && (
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/45 text-center">
          ΕΚΚΙΝΗΣΗ ΚΑΜΕΡΑΣ...
        </p>
      )}
      {error && (
        <p className="text-[11px] uppercase tracking-wider font-bold text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 text-center">
          {error}
        </p>
      )}
    </div>
  );
}
